import { useEffect, useState, memo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { TempLogo } from "@/assets/temp-logo";
import Footer from "@/components/Common/Footer";
// import { useAuth } from "../hooks/useAuth";
import { useLogin } from "../api/auth.queries";
import { extractErrorMessage } from "@/lib/errors";
import { Spinner } from "@/components/ui/spinner";

const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean().optional(),
});

export type LoginSchema = z.infer<typeof loginSchema>;

function LoginContainer() {
  const navigate = useNavigate();
  // const { isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const { mutate: login, isPending } = useLogin();
  const { setError } = form;

  useEffect(() => {
    localStorage.removeItem("password");
    localStorage.removeItem("email");

    const oldEmail = localStorage.getItem("email");
    if (oldEmail) {
      localStorage.setItem("rememberedEmail", oldEmail);
      localStorage.removeItem("email");
    }
  }, []);

  useEffect(() => {
    const remembered = localStorage.getItem("rememberMe") === "true";
    if (remembered) {
      form.setValue("rememberMe", true);
      form.setValue("email", localStorage.getItem("rememberedEmail") || "");
    }
  }, [form]);

  // Sync email with localStorage when "Remember Me" changes
  useEffect(() => {
    const sub = form.watch((values) => {
      if (values.rememberMe && values.email) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("rememberedEmail", values.email);
      } else if (!values.rememberMe) {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("rememberedEmail");
      }
    });
    return () => sub.unsubscribe();
  }, [form]);

  // if (isAuthenticated) {
  //   navigate({ to: "/" });
  //   return null;
  // }

  function onSubmit(values: LoginSchema) {
    login(
      { email: values.email, password: values.password },
      {
        onSuccess: () => {
          // Only store email if "Remember Me" is checked (NEVER store password!)
          if (values.rememberMe) {
            localStorage.setItem("rememberMe", "true");
            localStorage.setItem("rememberedEmail", values.email);
          } else {
            localStorage.removeItem("rememberMe");
            localStorage.removeItem("rememberedEmail");
          }
        },
        onError: (err) => {
          const message = extractErrorMessage(err);

          if (message.includes("password")) {
            setError("password", { type: "server", message });
          } else if (message.includes("email")) {
            setError("email", { type: "server", message });
          } else {
            setError("root", { type: "server", message });
          }
        },
      },
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between px-4 pb-8">
      {/* Main container */}
      <div className="flex w-full max-w-[420px] flex-grow flex-col justify-center gap-8">
        {/* Logo and App Name */}
        <div className="text-center">
          <div className="flex justify-center">
            <TempLogo width={68} height={44} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">metawhale</h1>
          <p className="text-center text-[10px] text-gray-500">
            {import.meta.env.VITE_APP_VERSION}
          </p>
        </div>

        {/* Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-0">
            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Email Address
                    <span className="text-sm text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="email@example.com"
                      className="h-[45px] w-full"
                      autoComplete="email"
                      variant={"filled"}
                    />
                  </FormControl>
                  <FormMessage className="text-xs text-red-600" />
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="mt-3 space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Password <span className="text-sm text-red-600">*</span>
                  </FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        {...field}
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••••••"
                        className="h-[45px] w-full"
                        autoComplete="current-password"
                        variant={"filled"}
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-1/2 right-3 h-6 w-6 -translate-y-1/2 text-neutral-800"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <i className="ri-eye-off-line"></i>
                      ) : (
                        <i className="ri-eye-line"></i>
                      )}
                      <span className="sr-only">
                        {showPassword ? "Hide password" : "Show password"}
                      </span>
                    </Button>
                  </div>
                  <FormMessage className="text-xs !text-red-600" />
                </FormItem>
              )}
            />

            {/* Remember + Forgot */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(val) => field.onChange(val === true)}
                      />
                    </FormControl>
                    <FormLabel
                      htmlFor="remember"
                      className="cursor-pointer text-sm font-semibold text-neutral-800"
                    >
                      Remember me
                    </FormLabel>
                  </FormItem>
                )}
              />

              <a
                onClick={() =>
                  navigate({
                    to: "/forgot-password",
                    search: {
                      email: undefined,
                      token: undefined,
                      user_id: undefined,
                    },
                  })
                }
                className="text-brand-600 cursor-pointer text-sm font-semibold hover:text-orange-600"
              >
                Forgot your password?
              </a>
            </div>

            {/* Login Button */}
            <div className="mt-10 space-y-3">
              <Button
                type="submit"
                className="text-md h-[48px] w-full leading-normal"
                disabled={isPending}
              >
                {isPending ? (
                  <div className="flex h-full items-center justify-center text-white">
                    <Spinner size="sm" className="stroke-white!" />
                  </div>
                ) : (
                  "Login"
                )}
              </Button>
              <Button
                className="text-md text-muted h-[48px] w-full leading-normal"
                disabled={isPending}
                variant="outline"
                onClick={() => navigate({ to: "/login/hash" })}
              >
                Login with Hash Key
              </Button>
            </div>
          </form>
        </Form>

        {/* Divider */}
        <div className="-my-5 flex items-center justify-center gap-2">
          <div className="flex-1 border-t border-neutral-500" />
          <p className="px-2 text-sm font-semibold whitespace-nowrap text-neutral-500">
            Don't have an account?
          </p>
          <div className="flex-1 border-t border-neutral-500" />
        </div>

        {/* Register Button */}
        <div className="text-center">
          <Button
            variant="outline"
            className="text-md text-muted flex h-[48px] w-full items-center justify-center border-neutral-400 leading-normal"
            onClick={() => navigate({ to: "/register" })}
          >
            Register
          </Button>
        </div>

        {/* Help Text */}
        <div className="text-center text-sm font-semibold text-neutral-800">
          If you have any questions, please contact <br />
          <a
            href="mailto:support@media-meter.com"
            className="text-brand-600 hover:text-orange-600"
          >
            support@media-meter.com
          </a>
          . We are happy to help!
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default memo(LoginContainer);
